import { useMemo } from "react";
import { EditorNodeType, getWorkflowNodeConfig } from "../nodes";
import { RegisteredNode } from "../lib/register-node";
import { TriggerNodeDto, WorkflowV2NodeDto } from "@babackapp/private-api-client/types";
import { resolveEditorNodeType } from "../utils/node-resolver";
import {
  useIsWorkflowsListingMode,
  useWorkflowForm,
  useWorkflowsEditorWorkflows,
} from "../context/workflows-editor-workflows-context";


type Node = WorkflowV2NodeDto | TriggerNodeDto;


export interface UseNodeByIdResult {
  node: Node | null;
  nodeConfig: RegisteredNode<unknown> | null;
  editorNodeType: EditorNodeType | null;
}


/**
 * Hook to retrieve a node by its ID along with its registered configuration.
 * Works in both edit mode (single workflow) and list mode (all workflows).
 */
export function useNodeById(nodeId: string): UseNodeByIdResult {
  const form = useWorkflowForm();
  const { workflows } = useWorkflowsEditorWorkflows();
  const isListMode = useIsWorkflowsListingMode();

  return useMemo(() => {
    let foundNode: Node | undefined;

    if (isListMode) {
      // In list mode: search across all workflows
      for (const workflow of workflows) {
        const nodes = [workflow.trigger, ...(workflow.definition?.nodes || [])];
        foundNode = nodes.find((n) => n?.id === nodeId) as Node | undefined;
        if (foundNode) break;
      }
    } else {
      // In edit mode: use form data for the current workflow
      const workflowData = form.getValues("workflow");
      const nodesList = [workflowData?.trigger, ...(workflowData?.definition?.nodes || [])];
      foundNode = nodesList.find((n) => n?.id === nodeId) as Node | undefined;
    }

    if (!foundNode) {
      return { node: null, nodeConfig: null, editorNodeType: null };
    }

    const editorNodeType = resolveEditorNodeType(foundNode);
    const nodeConfig = getWorkflowNodeConfig(editorNodeType);

    return { node: foundNode, nodeConfig, editorNodeType };
  }, [isListMode, workflows, form, nodeId]);
}
